import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { personalAreaAuthState } from '@atoms';
import useWindowDimensions from '@helpers/hooks/windowDemensions';
import {
  sendPersonalOtp,
  verifyPersonalOtp,
} from '@api';
import './style.css';

// Phone Verification View
const PhoneView = ({ onVerified }) => {
  const [step, setStep] = useState(1);
  const [channel, setChannel] = useState('sms'); // 'sms' | 'email'
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expiryTime, setExpiryTime] = useState(0);
  const [phoneFocused, setPhoneFocused] = useState(false);
  const codeRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((p) => p - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (expiryTime <= 0) return;
    const timer = setTimeout(() => setExpiryTime((p) => p - 1), 1000);
    return () => clearTimeout(timer);
  }, [expiryTime]);

  const formatPhone = (value) => {
    const d = value.replace(/\D/g, '');
    if (d.length <= 3) return d;
    if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw.length <= 10) {
      setPhone(formatPhone(raw));
      setError('');
    }
  };

  const contactValue = () =>
    channel === 'email' ? email.trim() : phone.replace(/\D/g, '');

  const contactValid = () => {
    if (channel === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 9 && digits.length <= 10;
  };

  const switchChannel = (next) => {
    if (next === channel) return;
    setChannel(next);
    setStep(1);
    setCode(['', '', '', '']);
    setError('');
    setExpiryTime(0);
    setResendCooldown(0);
  };

  const handleSendOtp = async () => {
    if (!contactValid()) {
      setError(channel === 'email' ? 'אימייל לא תקין' : 'טלפון לא תקין');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await sendPersonalOtp(contactValue(), channel);
      setStep(2);
      setResendCooldown(60);
      setExpiryTime(300);
      setTimeout(() => codeRefs[0].current?.focus(), 100);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשליחת הקוד');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = useCallback((index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 4);
      const newCode = ['', '', '', ''];
      for (let i = 0; i < 4; i++) newCode[i] = digits[i] || '';
      setCode(newCode);
      setError('');
      codeRefs[Math.min(digits.length, 3)].current?.focus();
      return;
    }
    const digit = value.replace(/\D/g, '');
    setCode(prev => {
      const n = [...prev];
      n[index] = digit;
      return n;
    });
    setError('');
    if (digit && index < 3) codeRefs[index + 1].current?.focus();
  }, [codeRefs]);

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs[index - 1].current?.focus();
    }
    if (e.key === 'Enter') handleVerify();
  };

  // Auto-verify when all 4 digits are entered
  useEffect(() => {
    if (code.every(d => d !== '') && code.length === 4 && !loading && expiryTime > 0) {
      handleVerify();
    }
  }, [code]);

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 4) {
      setError('יש להזין קוד בן 4 ספרות');
      return;
    }
    if (expiryTime <= 0) {
      setError('הקוד פג תוקף, בקשו קוד חדש');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await verifyPersonalOtp(contactValue(), fullCode, channel);
      onVerified(result.token, result.phoneNumber || contactValue());
    } catch (err) {
      setError(err.response?.data?.error || 'קוד שגוי');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setCode(['', '', '', '']);
    setError('');
    setLoading(true);
    try {
      await sendPersonalOtp(contactValue(), channel);
      setResendCooldown(60);
      setExpiryTime(300);
      codeRefs[0].current?.focus();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשליחת הקוד');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="pam-phone">
      {step !== 2 && <h3 className="pam-phone__title">היי, היית כאן בעבר?</h3>}
      {step === 2 && (
        <p className="pam-phone__subtitle">{`שלחנו קוד אימות ל-${channel === 'email' ? email.trim() : phone}`}</p>
      )}

      {step === 1 ? (
        <div className="pam-phone__form">
          <div className="pam-phone__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={channel === 'sms'}
              className={`pam-phone__tab${channel === 'sms' ? ' on' : ''}`}
              onClick={() => switchChannel('sms')}
            >
              סמס
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={channel === 'email'}
              className={`pam-phone__tab${channel === 'email' ? ' on' : ''}`}
              onClick={() => switchChannel('email')}
            >
              אימייל
            </button>
          </div>
          <p className="pam-phone__hint">הזינו את הפרטים שאיתם ביצעתם את ההזמנה</p>
          <div className={`pam-phone__input-wrap${phoneFocused ? ' focused' : ''}`}>
            <img
              className="pam-phone__input-icon"
              src={`../../assets/images/onboarding-${channel === 'email' ? 'email' : 'phone'}${phoneFocused ? '-focus' : ''}.svg`}
              alt=""
            />
            {channel === 'email' ? (
              <input
                type="email"
                className="pam-phone__input"
                placeholder="כתובת אימייל"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                onFocus={() => setPhoneFocused(true)}
                onBlur={() => setPhoneFocused(false)}
                dir="ltr"
                autoComplete="email"
                autoFocus
              />
            ) : (
              <input
                type="tel"
                className="pam-phone__input"
                placeholder="מספר טלפון"
                value={phone}
                onChange={handlePhoneChange}
                onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                onFocus={() => setPhoneFocused(true)}
                onBlur={() => setPhoneFocused(false)}
                dir="rtl"
                inputMode="numeric"
                maxLength={11}
                autoFocus
              />
            )}
          </div>
          {error && <p className="pam-phone__error">{error}</p>}
          <button
            className="pam-phone__btn"
            onClick={handleSendOtp}
            disabled={loading || !contactValid()}
          >
            {loading ? <span className="pam-spinner" /> : 'שלח קוד'}
          </button>
        </div>
      ) : (
        <div className="pam-phone__form">
          <div className="pam-phone__code-row" dir="ltr">
            {code.map((digit, i) => (
              <input
                key={i}
                ref={codeRefs[i]}
                type="text"
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                className={`pam-phone__code-input ${digit ? 'filled' : ''}`}
                value={digit}
                onChange={(e) => handleCodeChange(i, e.target.value)}
                onKeyDown={(e) => handleCodeKeyDown(i, e)}
                maxLength={i === 0 ? 4 : 1}
                autoFocus={i === 0}
              />
            ))}
          </div>

          {expiryTime > 0 && (
            <p className="pam-phone__timer">הקוד תקף ל-{formatTime(expiryTime)}</p>
          )}
          {expiryTime <= 0 && step === 2 && (
            <p className="pam-phone__timer pam-phone__timer--expired">הקוד פג תוקף</p>
          )}

          {error && <p className="pam-phone__error">{error}</p>}

          <button
            className="pam-phone__btn"
            onClick={handleVerify}
            disabled={loading || code.join('').length !== 4}
          >
            {loading ? <span className="pam-spinner" /> : 'אמת קוד'}
          </button>

          <div className="pam-phone__actions">
            <button className="pam-phone__link" onClick={handleResend} disabled={resendCooldown > 0}>
              {resendCooldown > 0
                ? `שליחה חוזרת בעוד ${resendCooldown} שניות`
                : 'שלח קוד חדש'}
            </button>
            <button className="pam-phone__link" onClick={() => { setStep(1); setCode(['','','','']); setError(''); setExpiryTime(0); setResendCooldown(0); }}>
              {channel === 'email' ? 'שינוי אימייל' : 'שינוי טלפון'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Main PersonalAreaModal Component — OTP only, always redirects to page
const PersonalAreaModal = ({ handleCloseModal }) => {
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const [auth, setAuth] = useRecoilState(personalAreaAuthState);
  const modalName = width < 767 ? 'personalAreaMobile' : 'personalArea';

  // If already verified, skip OTP and go straight to page
  useEffect(() => {
    const savedToken = localStorage.getItem('pa_token');
    const savedPhone = localStorage.getItem('pa_phone');
    if (savedToken && savedPhone) {
      if (!auth.isVerified) {
        setAuth({ isVerified: true, phoneNumber: savedPhone, token: savedToken });
      }
      handleCloseModal(modalName);
      navigate('/personal-area');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerified = (token, phone) => {
    localStorage.setItem('pa_token', token);
    localStorage.setItem('pa_phone', phone);
    setAuth({ isVerified: true, phoneNumber: phone, token });
    handleCloseModal(modalName);
    navigate('/personal-area');
  };

  const handleClose = () => {
    handleCloseModal(modalName);
  };

  return (
    <div className="pam">
      <div className="pam__header">
        <div className="pam__header-content">
          <span className="pam__header-title">אזור אישי</span>
        </div>
        <button className="pam__close" onClick={handleClose}>&times;</button>
      </div>
      <div className="pam__body">
        <PhoneView onVerified={handleVerified} />

        <div className="pam__privacy">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2"/>
            <path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
          <span>החיבור מאובטח · שימוש לצפייה בהזמנות בלבד</span>
        </div>
      </div>
    </div>
  );
};

export default PersonalAreaModal;
